import { useEffect, useState } from 'react';

import { CLICK_TO_BUY } from 'constants/clickToBuy';
import { APP_PERMISSIONS } from 'constants/permissions';
import Acl from 'modules/acl';

export function useC2BPermission({
    loggedUser,
    noSellItems = [],
    skus = [],
    totalEmployeeCount,
    type,
}) {
    const [hasPermission, setHasPermission] = useState(false);
    const [loading, setLoading] = useState(true);
    const [companyHasSEHPlusSKU, setCompanyHasSEHPlusSKU] = useState(false);
    const [companyHasWHPLearnSKU, setCompanyHasWHPLearnSKU] = useState(true);
    const [companyHasGHRCSKU, setCompanyHasGHRCSKU] = useState(false);
    const [companyHasLEARNCatalogsSKU, setCompanyHasLEARNCatalogsSKU] = useState(false);
    const [companyHasAnonymousReportingSKU, setCompanyHasAnonymousReportingSKU] = useState(false);

    useEffect(() => {
        function setPermission() {
            const isClientAdmin = Acl.check(APP_PERMISSIONS.systemCompanyAdmin);
            const hasEmployeeCountInRange =
                totalEmployeeCount >= CLICK_TO_BUY.SEHP.EMPLOYEE_COUNT.MIN_LIMIT &&
                totalEmployeeCount <= CLICK_TO_BUY.SEHP.EMPLOYEE_COUNT.MAX_LIMIT;

            const { company, info } = loggedUser;
            const { salesforceId: companySalesforceId } = company;
            const { salesforceId: userSalesforceId } = info;
            const hasValidSalesforceId = Boolean(companySalesforceId && userSalesforceId);
            const companyHasPremiumSKU = skus => {
                return Boolean(skus.map(({ key }) => key).includes('premium'));
            };
            const hasPremiumSKU = companyHasPremiumSKU(skus);

            const hasCommonSKUsRequirementMet =
                hasPremiumSKU && isClientAdmin && hasEmployeeCountInRange && hasValidSalesforceId;

            const companyHasSEHPlusSKU = skus => {
                return CLICK_TO_BUY.SEHP.SEH_PLUS_SKU_KEYS.every(skuKey =>
                    skus.some(({ key }) => key === skuKey)
                );
            };
            const hasSEHPlusSKU = companyHasSEHPlusSKU(skus);

            const getLicenseCodesAsStrings = items =>
                items.flatMap(({ licenseCodes }) => licenseCodes.map(code => code.toString()));

            const noSellLicenseCodes = getLicenseCodesAsStrings(noSellItems);

            const hasSEHPlusSKUInNoSellList = CLICK_TO_BUY.MARKET_PLACE.PRODUCTS.CORE.SEHP.LICENSE_CODES.every(
                licenseCode => noSellLicenseCodes.includes(licenseCode.toString())
            );
            const companyHasWHPLearnSKU = skus => {
                return CLICK_TO_BUY.WHP.WHP_SKU_KEYS.every(skuKey =>
                    skus.some(({ key }) => key === skuKey)
                );
            };
            const hasWHPLearnSKU = companyHasWHPLearnSKU(skus);
            const hasWHPLearnSKUInNoSellList = CLICK_TO_BUY.MARKET_PLACE.PRODUCTS.CORE.WHP.LICENSE_CODES.every(
                licenseCode => noSellLicenseCodes.includes(licenseCode.toString())
            );
            const companyHasGHRCSKU = skus => {
                return CLICK_TO_BUY.GHRC.GHRC_SKU_KEYS.every(skuKey =>
                    skus.some(({ key }) => key === skuKey)
                );
            };
            const hasGHRCSKU = companyHasGHRCSKU(skus);
            const hasGHRCSKUInNoSellList = CLICK_TO_BUY.MARKET_PLACE.PRODUCTS.CORE.GHRC.LICENSE_CODES.every(
                licenseCode => noSellLicenseCodes.includes(licenseCode.toString())
            );

            const companyHasLearnCatalogsSKU = skus => {
                return CLICK_TO_BUY.LEARN_CATALOGS.LEARN_CATALOGS_SKU_KEYS.every(skuKey =>
                    skus.some(({ key }) => key === skuKey)
                );
            };
            const hasLearnCatalogsSKU = companyHasLearnCatalogsSKU(skus);
            const hasLearnCatalogsSKUInNoSellList = CLICK_TO_BUY.MARKET_PLACE.PRODUCTS.CORE.LEARN_CATALOGS.LICENSE_CODES.every(
                licenseCode => noSellLicenseCodes.includes(licenseCode.toString())
            );

            const companyHasAnonymousReportingSKU = skus => {
                return CLICK_TO_BUY.ANONYMOUS_REPORTING.ANONYMOUS_REPORTING_SKU_KEYS.every(skuKey =>
                    skus.some(({ key }) => key === skuKey)
                );
            };
            const hasAnonymousReportingSKU = companyHasAnonymousReportingSKU(skus);
            const hasARSKUInNoSellList = CLICK_TO_BUY.MARKET_PLACE.PRODUCTS.CORE.ANONYMOUS_REPORTING.LICENSE_CODES.every(
                licenseCode => noSellLicenseCodes.includes(licenseCode.toString())
            );

            let hasValidPermissions = false;
            if (type === CLICK_TO_BUY.COMMON.TYPES.SEH_PLUS) {
                hasValidPermissions =
                    !hasSEHPlusSKUInNoSellList && hasCommonSKUsRequirementMet && !hasSEHPlusSKU;
            } else if (type === CLICK_TO_BUY.COMMON.TYPES.WHP) {
                hasValidPermissions =
                    !hasWHPLearnSKUInNoSellList && hasCommonSKUsRequirementMet && !hasWHPLearnSKU;
            } else if (type === CLICK_TO_BUY.COMMON.TYPES.GHRC) {
                hasValidPermissions =
                    !hasGHRCSKUInNoSellList && hasCommonSKUsRequirementMet && !hasGHRCSKU;
            } else if (type === CLICK_TO_BUY.COMMON.TYPES.LEARN_CATALOGS) {
                hasValidPermissions =
                    !hasLearnCatalogsSKUInNoSellList &&
                    hasCommonSKUsRequirementMet &&
                    !hasLearnCatalogsSKU;
            } else if (type === CLICK_TO_BUY.COMMON.TYPES.ANONYMOUS_REPORTING) {
                hasValidPermissions =
                    !hasARSKUInNoSellList &&
                    hasCommonSKUsRequirementMet &&
                    !hasAnonymousReportingSKU;
            }

            setHasPermission(hasValidPermissions);
            setCompanyHasSEHPlusSKU(hasSEHPlusSKU);
            setCompanyHasWHPLearnSKU(hasWHPLearnSKU);
            setCompanyHasGHRCSKU(hasGHRCSKU);
            setCompanyHasLEARNCatalogsSKU(hasLearnCatalogsSKU);
            setCompanyHasAnonymousReportingSKU(hasAnonymousReportingSKU);
            setLoading(false);
        }

        setPermission();
    }, [loggedUser, noSellItems, skus, totalEmployeeCount, type]);

    return {
        companyHasAnonymousReportingSKU,
        companyHasGHRCSKU,
        companyHasLEARNCatalogsSKU,
        companyHasSEHPlusSKU,
        companyHasWHPLearnSKU,
        hasPermission,
        loading,
    };
}
